/**
 * CallRecommend
 *
 * Spec 3, in the call.
 *
 * RecommendSheet (coach), redesign spec batch 5: "Recommend to Ana", one
 * offer of those the lesson allows (recommendOffers) with its
 * price and a check, a line in the coach's words, then Recommend. Each offer sends once
 * per call; one already sent says so. sendLiveRecommendation decides
 * everything (the window, spots, once per offer) and writes the copy.
 *
 * OfferSheet (player): opened from the banner's View, the coach's photo,
 * "Justin recommends", the offer large (Monthly Coaching or Live Video
 * Lesson), its price, the coach's note as a quote with the name, the terms
 * (Monthly Coaching's as the checkout shows them), then Apple Pay first,
 * the saved card's row or, with no card on file, the card field, and "Pay
 * $235 · Start Monthly Coaching". Close in the corner. No timer,
 * nothing sent on its own. On success it says Booked, and the call shows
 * Booked on both sides from the recommendation's status.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { Check, CheckCircle } from '@phosphor-icons/react';
import { stripePromise, formatStripeDeclineError } from '../services/stripe';
import { createLiveMembershipFn, confirmLiveMembershipFn } from '../services/liveMemberships';
import { getCoachOfferings } from '../services/coachOfferings';
import {
  confirmLiveRecommendedSingle, getLiveBookingContext, getLiveOfferSummary, liveLessonsErrorMessage, markLiveRecommendationBooked,
  purchaseLiveRecommendedSingle, sendLiveRecommendation,
  type LiveOfferSummary, type LiveRecommendation, type RecommendOffer,
} from '../services/liveLessons';
import { formatPrice } from '../utils/formatPrice';
import { walletCloser } from '../utils/livePayment';
import CallSheet from './CallSheet';
import Sheet from './Sheet';
import { CardSection, WalletButton, type CardSectionHandle } from './LivePayment';
import { BTN_PRIMARY } from './buttonStyles';
import ButtonSpinner from './ButtonSpinner';
import { busyStyle } from '../utils/busyStyle';
import { monthlyCoachingTerms } from '../utils/monthlyCoachingCopy';
import { CoachPhoto } from './liveBooking/CoachHeader';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { OFFER_TITLE } from '../utils/recommendOffers';

const GROUP = 'overflow-hidden rounded-[12px] bg-call-tile';
const ROW_TEXT = 'text-[17px] leading-[22px] tracking-[-0.43px] text-white';
const FOOTNOTE = 'text-[13px] leading-[18px] text-white/60';


// ── Coach ─────────────────────────────────────────────────────────────────────

export function RecommendSheet({
  bookingId, coachId, playerId, playerFirstName, offers, recommendations, onClose, onSent, onSending, onFailed,
}: {
  bookingId: string;
  coachId: string;
  playerId?: string;
  playerFirstName: string;
  /** What this lesson allows (recommendOffers): after an intro both, after a single Monthly Coaching. */
  offers: RecommendOffer[];
  recommendations: LiveRecommendation[];
  onClose: () => void;
  onSent: () => void;
  /** At the tap, before the server answers: the player's banner goes up at once over the room's channel. */
  onSending?: (offer: RecommendOffer, note: string, priceCents: number) => void;
  /** The send failed: the banner the tap put up comes down. */
  onFailed?: (offer: RecommendOffer) => void;
}) {
  const [summary, setSummary] = useState<LiveOfferSummary | null>(null);
  const [programCents, setProgramCents] = useState<number | null>(null);
  const [picked, setPicked] = useState<RecommendOffer | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What this lesson may recommend, from the server by the lesson's kind
  // (getLiveBookingContext): a Free Intro Call, a Live Video Lesson or
  // Monthly Coaching; a Live Video Lesson, Monthly Coaching; a Monthly
  // Coaching lesson, nothing. The room's own reading of the booking until
  // it answers, or if it cannot.
  const [allowed, setAllowed] = useState<RecommendOffer[] | null>(null);
  useEffect(() => {
    getLiveBookingContext(coachId, { playerId, bookingId })
      .then((c) => setAllowed(c.lesson?.recommendOffers ?? null))
      .catch(() => { /* the room's reading stands */ });
  }, [coachId, playerId, bookingId]);
  const shown = allowed ?? offers;

  useEffect(() => {
    getLiveOfferSummary(coachId).then(setSummary).catch(() => {});
    getCoachOfferings(coachId).then((offerings) => {
      const program = offerings.find((o) => o.offeringType === 'training_program' && o.isActive !== false);
      setProgramCents(program?.price ?? null);
    }).catch(() => {});
  }, [coachId]);

  const sentFor = (offer: RecommendOffer) => recommendations.find((r) => r.offer === offer);
  const detail = (offer: RecommendOffer): string => {
    const sent = sentFor(offer);
    if (sent) return sent.status === 'booked' ? 'Booked' : 'Sent';
    if (offer === 'single') return summary ? formatPrice(summary.singlePriceCents) : '';
    if (offer === 'membership') {
      if (summary?.spotsLeft === 0) return 'No spots left';
      return summary ? `${formatPrice(summary.membershipPriceCents)}/mo` : '';
    }
    return programCents != null ? formatPrice(programCents) : '';
  };

  const send = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    const cents = picked === 'single' ? summary?.singlePriceCents : picked === 'membership' ? summary?.membershipPriceCents : programCents;
    if (cents != null) onSending?.(picked, note.trim(), cents);
    try {
      await sendLiveRecommendation({ bookingId, offer: picked, note: note.trim() || undefined });
      onSent();
    } catch (err) {
      onFailed?.(picked);
      setError(liveLessonsErrorMessage(err, 'Could not send. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CallSheet title={`Recommend to ${playerFirstName}`} onClose={onClose} busy={busy}>
      {shown.length === 0 && (
        <p className={FOOTNOTE}>{playerFirstName} has Monthly Coaching with you already.</p>
      )}
      {shown.length > 0 && <div className={GROUP} role="radiogroup" aria-label="Offer">
        {shown.map((offer, i) => {
          const title = OFFER_TITLE[offer];
          const done = !!sentFor(offer);
          const on = picked === offer;
          return (
            <div key={offer} className="pl-4">
              <button
                type="button"
                role="radio"
                aria-checked={on}
                disabled={done}
                onClick={() => { setPicked(offer); setError(null); }}
                className={`ios-press flex h-[52px] w-full items-center gap-3 pr-4 text-left disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/40 ${i ? 'border-t border-white/10' : ''}`}
              >
                <span className={`flex-1 ${ROW_TEXT} ${done ? 'text-white/40' : ''}`}>{title}</span>
                <span className="text-[17px] leading-[22px] text-white/60 tabular-nums">{detail(offer)}</span>
                <span className="flex w-5 justify-end">
                  {on && <Check size={18} weight="bold" color="#FFFFFF" aria-hidden="true" />}
                </span>
              </button>
            </div>
          );
        })}
      </div>}

      <div className={`mt-6 ${GROUP}`}>
        <input
          type="text"
          value={note}
          maxLength={140}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a line in your words (optional)"
          aria-label="Note for the player"
          className="block h-[52px] w-full bg-transparent px-4 text-[17px] leading-[20px] text-white placeholder:text-white/40 focus:outline-none"
        />
      </div>
      <p className={`mt-2 px-4 ${FOOTNOTE}`}>
        {playerFirstName} sees this over the call and can book it right there. Each offer sends once.
      </p>

      {error && <p className="mt-3 px-4 text-[13px] leading-[18px] text-white/85">{error}</p>}
      <button type="button" onClick={send} disabled={!picked || busy} aria-busy={busy}
        style={busyStyle(busy)} className={`${BTN_PRIMARY} mt-6 w-full`}>
        {busy ? <ButtonSpinner /> : 'Recommend'}
      </button>
    </CallSheet>
  );
}

// ── Player ────────────────────────────────────────────────────────────────────

export function OfferSheet(props: {
  bookingId: string;
  recommendation: LiveRecommendation;
  /** Shown from the coach's tap, before the server's record: Pay waits for it. */
  pending?: boolean;
  coachName: string;
  coachAvatarUrl?: string;
  /** `call` over the call canvas; `light` on the Delivered card after it. */
  theme?: 'call' | 'light';
  onPaid: () => void;
  onClose: () => void;
}) {
  return (
    <Elements stripe={stripePromise}>
      <OfferSheetInner {...props} />
    </Elements>
  );
}

function OfferSheetInner({
  bookingId, recommendation: rec, pending = false, coachName, coachAvatarUrl, theme = 'call', onPaid, onClose,
}: {
  bookingId: string;
  recommendation: LiveRecommendation;
  pending?: boolean;
  coachName: string;
  coachAvatarUrl?: string;
  theme?: 'call' | 'light';
  onPaid: () => void;
  onClose: () => void;
}) {
  const light = theme === 'light';
  const t = {
    primary: light ? 'text-db-text-1' : 'text-white',
    copy: light ? 'text-db-text-2' : 'text-white/70',
    footnote: light ? 'text-[13px] leading-[18px] text-db-text-2' : FOOTNOTE,
    group: light ? 'overflow-hidden rounded-[12px] bg-white' : GROUP,
    error: light ? 'text-danger' : 'text-white/85',
  };
  const Shell = light ? Sheet : CallSheet;
  const stripe = useStripe();
  const cardRef = useRef<CardSectionHandle | null>(null);
  const [walletShown, setWalletShown] = useState<boolean | null>(null);
  // The saved card's row is known (CardSection): until then Pay waits, so the first tap pays the card it shows.
  const [cardKnown, setCardKnown] = useState(false);
  const [summary, setSummary] = useState<LiveOfferSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const coachFirstName = coachName.split(' ')[0] || coachName;
  const coach = useUserIdentity(rec.coachId, 'coach');
  const booked = rec.status === 'booked';
  const buyable = rec.offer !== 'program';
  const price = rec.offer === 'membership' ? `${formatPrice(rec.priceCents)} a month` : formatPrice(rec.priceCents);
  // The terms: Monthly Coaching's as the checkout shows them, word for word
  // (the discount, the three lines, the spots left); the server's lines
  // otherwise. The coach's note is not a term: it shows as a quote.
  const note = rec.note?.trim() ?? '';
  const serverLines = rec.copyLines.filter((l) => l !== note && !/spots? left/i.test(l));
  const isMembership = rec.offer === 'membership';
  useEffect(() => {
    if (!isMembership) return;
    let live = true;
    getLiveOfferSummary(rec.coachId).then((s) => { if (live) setSummary(s); }).catch(() => { /* the lines without spots */ });
    return () => { live = false; };
  }, [isMembership, rec.coachId]);
  const terms = isMembership ? monthlyCoachingTerms(rec.priceCents, coachFirstName, summary) : null;
  const lines = terms ? terms.lines : serverLines;
  const title = OFFER_TITLE[rec.offer];
  // The price on the button, as on the booking pay page: one tap charges the card shown above it.
  const buttonLabel = isMembership ? `Pay ${formatPrice(rec.priceCents)} · Start Monthly Coaching` : `Pay ${price} · Book Lesson`;

  /**
   * Pays with a payment method from the wallet, or (none given) with the
   * card section: the saved card, charged by the server, or a new card.
   */
  const pay = useCallback(async (confirmWith: (clientSecret: string) => Promise<void>, paymentMethodId?: string) => {
    // Held open from here, so it stays to say Booked when the status flips.
    onPaid();
    if (rec.offer === 'single') {
      const res = await purchaseLiveRecommendedSingle({ bookingId, paymentMethodId });
      if (res.status === 'requires_payment') {
        if (res.clientSecret) await confirmWith(res.clientSecret);
        await confirmLiveRecommendedSingle({ reservationId: res.reservationId });
      }
    } else if (rec.offer === 'membership' && rec.offeringId) {
      const res = await createLiveMembershipFn({ offeringId: rec.offeringId, paymentMethodId, sessionId });
      if (!res.alreadyActive && res.clientSecret) await confirmWith(res.clientSecret);
      await confirmLiveMembershipFn(res.membershipId);
      await markLiveRecommendationBooked({ bookingId, offer: 'membership', membershipId: res.membershipId });
    }
  }, [bookingId, rec.offer, rec.offeringId, sessionId, onPaid]);

  const payWithCard = async () => {
    const handle = cardRef.current;
    if (!handle) return;
    setBusy(true);
    setError(null);
    try {
      await pay((secret) => handle.confirmCard(secret), handle.paymentMethodId);
    } catch (err) {
      setError(liveLessonsErrorMessage(err, err instanceof Error ? err.message : 'Payment failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const payWithWallet = useCallback(async (ev: PaymentRequestPaymentMethodEvent) => {
    if (!stripe) { ev.complete('fail'); return; }
    const closeWallet = walletCloser(ev);
    setBusy(true);
    setError(null);
    try {
      await pay(async (secret) => {
        const first = await stripe.confirmCardPayment(secret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
        if (first.error) {
          closeWallet('fail');
          throw new Error(formatStripeDeclineError(first.error.decline_code, first.error.message));
        }
        closeWallet('success');
        if (first.paymentIntent?.status === 'requires_action') {
          const second = await stripe.confirmCardPayment(secret);
          if (second.error) throw new Error(formatStripeDeclineError(second.error.decline_code, second.error.message));
        }
      });
      closeWallet('success');
    } catch (err) {
      closeWallet('fail');
      setError(liveLessonsErrorMessage(err, err instanceof Error ? err.message : 'Payment failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  }, [stripe, pay]);

  return (
    <Shell title="" onClose={onClose} busy={busy} dismissLabel="Close">
      <div className="flex flex-col items-center text-center">
        <CoachPhoto url={coachAvatarUrl ?? coach?.avatarUrl} name={coachName} size={64} />
        <p className={`mt-3 ${t.footnote}`}>{coachFirstName} recommends</p>
        <h2 className={`mt-0.5 text-[28px] leading-[34px] font-bold ${t.primary}`}>{title}</h2>
        <p className={`mt-1 text-[17px] leading-[22px] tabular-nums ${t.primary}`}>{price}</p>
        {terms?.discount && <p className={`mt-0.5 text-[15px] leading-[20px] ${t.copy}`}>{terms.discount}</p>}
      </div>

      {note && (
        <figure className="mt-5">
          <blockquote className={`text-[17px] leading-[22px] ${t.primary}`}>“{note}”</blockquote>
          <figcaption className={`mt-1 ${t.footnote}`}>{coachName}</figcaption>
        </figure>
      )}

      <div className="mt-4 space-y-2">
        {lines.map((line) => (
          <p key={line} className={`text-[15px] leading-[20px] ${t.copy}`}>{line}</p>
        ))}
        {terms?.spots && <p className={`pt-1 text-[15px] leading-[20px] ${t.primary}`}>{terms.spots}</p>}
      </div>

      <div className="mt-6">
        {booked ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center">
            <CheckCircle size={32} weight="fill" color="#34C759" aria-hidden="true" />
            <p className={`text-[17px] leading-[22px] font-semibold ${t.primary}`}>Booked</p>
            <p className={t.footnote}>A receipt is in your messages with {coachFirstName}.</p>
          </div>
        ) : !buyable ? (
          <p className={t.footnote}>Programs are bought from {coachFirstName}&apos;s profile. You can buy it there after the call.</p>
        ) : (
          <>
            {/* Apple Pay first; then the saved card's row, or with no card on file the card field, above the button. */}
            <WalletButton
              label={`${title} with ${coachName}`}
              priceCents={rec.priceCents}
              disabled={busy || pending}
              theme={light ? 'light' : 'dark'}
              className="mb-3"
              buttonType="default"
              onPaymentMethod={payWithWallet}
              onAvailable={setWalletShown}
            />
            {/* Drawn at once, its row grey until the saved card list answers, so the card shows before any tap. */}
            <div className={t.group}>
              <CardSection disabled={busy} handleRef={cardRef} theme={light ? 'light' : 'dark'} onSavedCard={() => setCardKnown(true)} />
            </div>
            <button type="button" onClick={payWithCard} disabled={busy || pending || walletShown === null || !cardKnown} aria-busy={busy}
              style={busyStyle(busy)} className={`${BTN_PRIMARY} mt-4 w-full`}>
              {busy ? <ButtonSpinner /> : buttonLabel}
            </button>
          </>
        )}
        {error && <p className={`mt-3 text-center text-[13px] leading-[18px] ${t.error}`}>{error}</p>}
      </div>
    </Shell>
  );
}
